import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormControlLabel,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import InfoIcon from "@mui/icons-material/Info";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import AlertComponent from "../../subcomponents/AlertComponent";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { retailRoleSetupTooltip } from "../../../constants/TooltipInformation";
import { fieldValidationHelperText } from "../../../constants/validationHelperText";
import { RetailPropertyEnum } from "../../../constants/NavbarList";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const parseActiveField = (value: unknown): boolean => {
  if (value === false || value === "No" || value === "no") return false;
  if (value === true || value === "Yes" || value === "yes") return true;
  return true;
};

const RetailRoleSetup = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [roleCode, setRoleCode] = useState<string | null>(null);
  const [roleName, setRoleName] = useState<string | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const [roleCodeError, setRoleCodeError] = useState<boolean>(false);
  const [roleNameError, setRoleNameError] = useState<boolean>(false);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [retailRoles, setRetailRoles] = useState<any>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const payload = {
    "Role Code": roleCode,
    "Role Name": roleName,
    Active: active ? "Yes" : "No",
  };

  const validateInput = () => {
    setRoleCodeError(false);
    setRoleNameError(false);
    if (!roleCode || String(roleCode).trim() === "") {
      setRoleCodeError(true);
      return false;
    }
    if (
      !validateString(roleCode, validationLength.roleCode, validationType.NAME)
    ) {
      setRoleCodeError(true);
      return false;
    }
    if (!roleName || String(roleName).trim() === "") {
      setRoleNameError(true);
      return false;
    }
    if (
      !validateString(
        roleName,
        validationLength.retailRoleName,
        validationType.NAME,
      )
    ) {
      setRoleNameError(true);
      return false;
    }
    return true;
  };

  const updateRetailRoles = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(
        retailRoles.filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.ROLECODE,
        roleCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setRoleCodeError(false);
    setRoleNameError(false);
    setRetailRoles((prev: any) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editRetailRoles(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${RetailPropertyEnum.roleSetup} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addRetailRoles = () => {
    if (!validateInput()) return;
    if (checkDuplicate(retailRoles, DNDFieldsEnum.ROLECODE, roleCode)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setRetailRoles((prev: any) =>
      updateData(
        prev !== null && prev?.length
          ? [...prev, { id: retailRoles.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editRetailRoles(null);
    setEditClicked(false);
  };

  const editRetailRoles = (list: any) => {
    if (list == null) {
      setSelectedId(null);
      setEditClicked(false);
      setRoleCode(null);
      setRoleName(null);
      setActive(true);
      setRoleCodeError(false);
      setRoleNameError(false);
      return;
    }
    list?.id && setSelectedId(list.id);
    setEditClicked(true);
    setRoleCode(list?.["Role Code"] ?? null);
    setRoleName(list?.["Role Name"] ?? null);
    setActive(parseActiveField(list?.["Active"]));
    setRoleCodeError(false);
    setRoleNameError(false);
  };

  const deleteRetailRoles = (id: number) => {
    setRetailRoles(deleteItem(retailRoles, id));
  };

  useEffect(() => {
    if (fieldValues !== undefined && fieldValues !== null) {
      setRetailRoles(fieldValues);
    }
  }, []);

  useEffect(() => {
    data(retailRoles);
    if (editClicked) {
      setSelectedId(checkData(retailRoles, selectedId) ? selectedId : null);
    }
  }, [retailRoles]);

  const roleCodeHelperText = roleCodeError
    ? !roleCode || String(roleCode).trim() === ""
      ? validationMessages.fieldRequired
      : validationMessages.retailRoleSetupCode
    : fieldValidationHelperText.retailRoleSetupCode;

  const roleNameHelperText = roleNameError
    ? !roleName || String(roleName).trim() === ""
      ? validationMessages.fieldRequired
      : validationMessages.retailRoleSetupName
    : fieldValidationHelperText.retailRoleSetupName;

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Role Setup
              <Tooltip title={tooltipComponent(retailRoleSetupTooltip.header)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton click={() => editRetailRoles(null)} />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editRetailRoles(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Role Code
              <Tooltip
                title={tooltipComponent(retailRoleSetupTooltip.roleCode)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailRoleCode"
              variant="outlined"
              placeholder="Role Code"
              value={roleCode ?? ""}
              helperText={roleCodeHelperText}
              error={roleCodeError}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setRoleCode(e.target.value || null);
                setRoleCodeError(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Role Name
              <Tooltip
                title={tooltipComponent(retailRoleSetupTooltip.roleName)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="retailRoleName"
              variant="outlined"
              placeholder="Role Name"
              value={roleName ?? ""}
              helperText={roleNameHelperText}
              error={roleNameError}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setRoleName(e.target.value || null);
                setRoleNameError(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Active?
              <Tooltip title={tooltipComponent(retailRoleSetupTooltip.active)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label=""
            />
          </Stack>

          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={() =>
                selectedId ? updateRetailRoles() : addRetailRoles()
              }
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Role
            </Button>
          </Stack>

          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">Role Setup Available</Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {retailRoles?.length ? (
                <DraggableItemList
                  itemData={retailRoles}
                  onOrderUpdate={(order) => setRetailRoles(order)}
                  onEdit={(info) => {
                    editRetailRoles(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteRetailRoles(id)}
                  code={DNDFieldsEnum.ROLECODE}
                  name={DNDFieldsEnum.ROLENAME}
                />
              ) : (
                <Typography variant="body2">
                  No Role Setup Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default RetailRoleSetup;
